import React, { useEffect, useState } from 'react'
import Navbar from '../../components/Navbar'
import styles from './ToDo.module.css'
import { Button, Divider, Empty, Input, Modal, Select, Tag, Tooltip, message } from 'antd'
import { useNavigate } from 'react-router';
import { getErrorMessage } from '../../util/GetError.js';
import { getUserDetails } from '../../util/GetUser';
import ToDoServices from '../../services/toDoServices';
import { CheckCircleFilled, CheckCircleOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';

const DAYS_OF_WEEK = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
];

function ToDoList() {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [isAdding, setIsAdding] = useState(false);
    const [loading, setLoading] = useState(false);
    const [allToDo, setAllToDo] = useState([]);
    const navigate = useNavigate();
    const [currentEditItem, setCurrentEditItem] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [updatedTitle, setUpdatedTitle] = useState("");
    const [updatedDescription, setUpdatedDescription] = useState("");
    const [updatedStatus, setUpdatedStatus] = useState("");
    const [currentTaskType, setCurrentTaskType] = useState("all");
    const [currentToDoTask, setCurrentToDoTask] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [taskDays, setTaskDays] = useState({});
    const [selectedDay, setSelectedDay] = useState("Monday");
    const [updatedDay, setUpdatedDay] = useState("Monday");
    const firstRowDays = ["Monday", "Tuesday", "Wednesday", "Thursday"];
    const secondRowDays = ["Friday", "Saturday", "Sunday"];

    const getAllToDo = async () => {
        try {
            const user = getUserDetails();
            if (!user?.userId) {
                navigate('/login');
                return;
            }
            const response = await ToDoServices.getAllToDo(user.userId);
            setAllToDo(response.data);
            setCurrentToDoTask(response.data); // Set initial tasks
        } catch (err) {
            console.log(err);
            message.error(getErrorMessage(err));
        }
    };

    useEffect(() => {
        const userId = getUserDetails()?.userId;
        const savedTaskDays = localStorage.getItem(`taskDays_${userId}`);
        if (savedTaskDays) {
            setTaskDays(JSON.parse(savedTaskDays));
        }
    }, []);

    useEffect(() => {
        const userId = getUserDetails()?.userId;
        if (Object.keys(taskDays).length > 0) { 
            localStorage.setItem(`taskDays_${userId}`, JSON.stringify(taskDays));
            console.log("Saved task days:", taskDays); 
        }
    }, [taskDays]);

    useEffect(() => {
        getAllToDo();
    }, [navigate]);

    useEffect(() => {
        // First filter by task type
        let filteredByType = [...allToDo];
        if (currentTaskType === 'incomplete') {
            filteredByType = allToDo.filter(item => !item.isCompleted);
        } else if (currentTaskType === 'completed') {
           filteredByType = allToDo.filter(item => item.isCompleted);
        }
        
        // Then filter by search query
        if (searchQuery.trim()) {
                const query = searchQuery.toLowerCase();
                filteredByType = filteredByType.filter(item =>
                    item.title.toLowerCase().includes(query) ||
                    item.description.toLowerCase().includes(query)
                );
            }
            
                setCurrentToDoTask(filteredByType);
            }, [allToDo, currentTaskType, searchQuery]);

    const filterTasks = (taskType) => {
        switch (taskType) {
            case 'all':
                setCurrentToDoTask(allToDo);
                break;
            case 'incomplete':
                setCurrentToDoTask(allToDo.filter(item => !item.isCompleted));
                break;
            case 'completed':
                setCurrentToDoTask(allToDo.filter(item => item.isCompleted));
                break;
            default:
                setCurrentToDoTask(allToDo);
        }
    };

    const handleSubmitTask = async () => {
        try {
            setLoading(true);
            const userId = getUserDetails()?.userId;
    
            const data = {
                title,
                description,
                isCompleted: false,
                createdBy: userId
            };
    
            // Create task in backend
            const response = await ToDoServices.createToDo(data);
            const newTaskId = response.data._id;
    
            // Update taskDays synchronously
            const updatedDays = { ...taskDays, [newTaskId]: selectedDay };
            setTaskDays(updatedDays); // Update state
            localStorage.setItem(`taskDays_${userId}`, JSON.stringify(updatedDays)); // Persist to localStorage
    
            message.success("To Do Task Added Successfully!");
            setIsAdding(false);
            setTitle("");
            setDescription("");
            setSelectedDay("Monday"); // Reset to default explicitly
    
            // Refresh tasks
            await getAllToDo();
        } catch (err) {
            console.log(err);
            message.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const getFormattedDate = (value) => {
        let date = new Date(value);
        let dateString = date.toDateString();
        let hh = date.getHours();
        let min = date.getMinutes();
        let ss = date.getSeconds();
        let finalDate = `${dateString} at ${hh}:${min}:${ss}`;
        return finalDate;
    }

    const handleEdit = (item) => {
        setCurrentEditItem(item);
        setUpdatedTitle(item?.title);
        setUpdatedDescription(item?.description);
        setUpdatedStatus(item?.isCompleted);
        setUpdatedDay(taskDays[item._id] || 'Monday');
        setIsEditing(true);
    };

    const handleDelete = async (item) => {
        try {
            const response = await ToDoServices.deleteToDo(item._id);
            // Remove the day assignment when task is deleted
            setTaskDays(prev => {
                const newTaskDays = { ...prev };
                delete newTaskDays[item._id];
                return newTaskDays;
            });
            message.success(`${item.title} Deleted Successfully!`);
            getAllToDo();
        } catch (err) {
            console.log(err);
            message.error(getErrorMessage(err));
        }
    };

    const groupTasksByDay = (tasks) => {
        const grouped = {};
        DAYS_OF_WEEK.forEach(day => {
            grouped[day] = tasks.filter(task => (taskDays[task._id] || 'Monday') === day);
        });
        return grouped;
    };

    const handleUpdateStatus = async (id, status) => {
        try {
            const response = await ToDoServices.updateToDo(id, { isCompleted: status });
            message.success("Task Status Updated Successfully!");
            getAllToDo();
        } catch (err) {
            console.log(err);
            message.error(getErrorMessage(err));
        }
    }

    const handleUpdateTask = async () => {
        try {
            setLoading(true);
            const data = {
                title: updatedTitle,
                description: updatedDescription,
                isCompleted: updatedStatus
            };
            await ToDoServices.updateToDo(currentEditItem?._id, data);
    
            // Update taskDays state and persist to localStorage
            setTaskDays(prev => {
                const updatedDays = { ...prev, [currentEditItem._id]: updatedDay };
                localStorage.setItem(`taskDays_${getUserDetails()?.userId}`, JSON.stringify(updatedDays));
                console.log("Updated task days after edit:", updatedDays); // Debugging
                return updatedDays;
            });
    
            message.success(`${currentEditItem?.title} Updated Successfully!`);
            setLoading(false);
            setIsEditing(false);
            getAllToDo();
        } catch (err) {
            console.log(err);
            setLoading(false);
            message.error(getErrorMessage(err));
        }
    };

    const handleTypeChange = (value) => {
        setCurrentTaskType(value);
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    return (
        <>
            <Navbar active={"myTask"} />
            <section className={styles.toDoWrapper}>
                <div className={styles.toDoHeader}>
                    <h2>Your Tasks</h2>
                    <Input style={{ width: '50%' }} placeholder='Search Your Task Here...' onChange={handleSearch} allowClear/>
                    <div>
                        <Button onClick={() => setIsAdding(true)} type="primary" size="large">Add Task</Button>
                        <Select
                            value={currentTaskType}
                            style={{ width: 180, marginLeft: '10px' }}
                            onChange={handleTypeChange}
                            size="large"
                            options={[
                                { value: 'all', label: "All" },
                                { value: 'incomplete', label: "Incomplete" },
                                { value: 'completed', label: "Completed" }
                            ]}
                        />
                    </div>
                </div>
                <Divider />
                <div className={styles.toDoListContainer}>
                    {[['Monday', 'Tuesday', 'Wednesday', 'Thursday'], ['Friday', 'Saturday', 'Sunday']].map((weekGroup, index) => (
                        <div key={index} className={styles.toDoRow} style={{ marginBottom: index === 0 ? '3rem' : '0' }}>
                            {weekGroup.map(day => (
                                <div key={day} className={styles.daySection}>
                                    <h2 className={styles.dayHeader}>{day}</h2>
                                    <div className={styles.dayTasks}>
                                        {groupTasksByDay(currentToDoTask)[day].length === 0 ? (
                                            <Empty description={`No tasks for ${day}`} />
                                        ) : (
                                            groupTasksByDay(currentToDoTask)[day].map((item) => (
                                                <div key={item?._id} className={styles.toDoCard} style={{ marginBottom: '1.5rem' }}>
                                                    <div>
                                                        <div className={styles.toDoCardHeader}>
                                                            <h3>{item?.title}</h3>
                                                            {item?.isCompleted ? 
                                                                <Tag color="cyan">Completed</Tag> : 
                                                                <Tag color="red">Incomplete</Tag>
                                                            }
                                                        </div>
                                                        <p>{item?.description}</p>
                                                    </div>
                                                    <div className={styles.toDoCardFooter}>
                                                        <Tag>{getFormattedDate(item?.createdAt)}</Tag>
                                                        <div className={styles.toDoFooterAction}>
                                                            <Tooltip title="Edit Task?">
                                                                <EditOutlined onClick={() => handleEdit(item)} className={styles.actionIcon} />
                                                            </Tooltip>
                                                            <Tooltip title="Delete Task?">
                                                                <DeleteOutlined onClick={() => handleDelete(item)} style={{ color: 'red' }} className={styles.actionIcon} />
                                                            </Tooltip>
                                                            {item?.isCompleted ?
                                                                <Tooltip title="Mark as Incomplete">
                                                                    <CheckCircleFilled onClick={() => handleUpdateStatus(item._id, false)} style={{ color: 'green' }} className={styles.actionIcon} />
                                                                </Tooltip> :
                                                                <Tooltip title="Mark as Completed">
                                                                    <CheckCircleOutlined onClick={() => handleUpdateStatus(item._id, true)} className={styles.actionIcon} />
                                                                </Tooltip>
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                <Modal confirmLoading={loading} title="Add New To Do Task" open={isAdding} onOk={handleSubmitTask} onCancel={() => setIsAdding(false)}>
                    <Input style={{ marginBottom: '1rem' }} placeholder='Title' value={title} onChange={(e) => setTitle(e.target.value)} />
                    <Input.TextArea style={{ marginBottom: '1rem' }} placeholder='Description' value={description} onChange={(e) => setDescription(e.target.value)} />
                </Modal>
                <Modal confirmLoading={loading} title={`Update ${currentEditItem.title}`} open={isEditing} onOk={handleUpdateTask} onCancel={() => setIsEditing(false)}>
                    <Input style={{ marginBottom: '1rem' }} placeholder='Updated Title' value={updatedTitle} onChange={(e) => setUpdatedTitle(e.target.value)} />
                    <Input.TextArea style={{ marginBottom: '1rem' }} placeholder='Updated Description' value={updatedDescription} onChange={(e) => setUpdatedDescription(e.target.value)} />
                    <Select
                        style={{ width: '100%', marginBottom: '1rem' }}
                        onChange={(value) => setUpdatedDay(value)}
                        value={updatedDay}
                        options={DAYS_OF_WEEK.map(day => ({ value: day, label: day }))}
                    />
                    <Select
                        style={{ width: '35%' }}
                        onChange={(value) => setUpdatedStatus(value)}
                        value={updatedStatus}
                        options={[
                            {
                                value: false,
                                label: 'Not Completed',
                            },
                            {
                                value: true,
                                label: 'Completed',
                            },
                        ]}
                    />
                </Modal>
            </section>
        </>
    )
}

export default ToDoList